import { differenceInMinutes } from "date-fns";

import {
  createAppSelector,
  selectClassSessionSummaries,
  selectClassSessions,
  selectCurrentCourseId,
  selectCurrentUserId,
} from "./base";

import type { ClassSessionType } from "components/server-types";
import { ClassSessionCategory } from "components/server-types";
import { RootState } from "store";
import { getActiveClassSession, isClassSessionActive } from "utils/class-session";

export const selectClassSessionByCourseId = createAppSelector(
  [selectClassSessions, (state, courseId: string | null) => courseId],
  (classSessions, courseId) => getActiveClassSession(classSessions, courseId),
);

export const selectClassSessionCategoryByCourseId = createAppSelector(
  [selectClassSessionByCourseId],
  (classSession) => classSession?.category || null,
);

export const selectActiveClassSession = createAppSelector(
  [selectClassSessions, selectCurrentCourseId],
  (classSessions, courseId) => getActiveClassSession(classSessions, courseId),
);

export const selectActiveClassSessionId: (state: RootState) => string | null = createAppSelector(
  [selectActiveClassSession],
  (classSession: ClassSessionType | null) => classSession?.id || null,
);

export const selectActiveClassSessionCategory = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.category || null,
);

export const selectIsClassSessionInPerson = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.category === ClassSessionCategory.IN_PERSON,
);

export const selectIsClassSessionVirtual = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.category === ClassSessionCategory.VIRTUAL,
);

export const selectIsPracticeClassSession = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.is_practice || false,
);

// Separate from selectActiveClassSession just to be more specific and prevent re-renders
export const selectIsPresentationActive = createAppSelector(
  [selectActiveClassSession],
  (activeSession) => Boolean(activeSession?.presented_page_id),
);

export const selectPresentedPageId = createAppSelector(
  [selectActiveClassSession],
  (activeSession) => activeSession?.presented_page_id || null,
);

export const selectPresentingUserId = createAppSelector(
  [selectActiveClassSession],
  (activeSession) => activeSession?.presenting_user_id || null,
);

export const selectPresentingSessions = createAppSelector(
  [selectClassSessions, selectCurrentUserId],
  (classSessions, userId) =>
    classSessions.filter((cs) => cs.presenting_user_id === userId && isClassSessionActive(cs)),
);

const MIN_DURATION_CLASS_MINUTES = 5;
export const selectAllVisibleClassSessionsForCurrentCourse = createAppSelector(
  [selectClassSessions, selectCurrentCourseId],
  (classSessions, courseId) =>
    classSessions.filter((cs) => {
      if (cs.section_id !== courseId) return false;
      if (cs.is_hidden_from_session_logs) return false;
      if (!cs.started_at || !cs.ended_at) return false;
      const duration = differenceInMinutes(cs.ended_at, cs.started_at);
      if (duration < MIN_DURATION_CLASS_MINUTES) return false;
      return true;
    }),
);

export const selectClassSessionSummariesForCurrentCourse = createAppSelector(
  [selectClassSessionSummaries, selectCurrentCourseId],
  (classSessionSummaries, courseId) =>
    classSessionSummaries.filter((cs) => cs.section_id === courseId),
);
